import { useEffect, useState } from 'react';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// Componentes
import { DragDropOperatorProvider, useDragDropOperator } from './DragDropOperatorProvider';
import { UserSelector } from '../asignaciones-usuario-maquina/UserSelector'; // Reutilizar selector existente
import { OperatorPool } from './OperatorPool';
import { DraggableOperator } from './DraggableOperator';
import { Button, LoadingSpinner } from '../../ui';

// Servicios y hooks
import { AsignacionesUsuarioOperadorService } from '../../../services/asignacionesUsuarioOperadorService';
import { useUsuariosUAU } from '../../../hooks/useUsuariosUAU';
import { useEmpresasContext } from '../../../context/EmpresasContext';

// ===============================
// COMPONENTE INTERNO (CON PROVIDER)
// ===============================

function AdminUserOperatorViewContent() {
  const { t } = useTranslation();
  const { state, dispatch, moveOperatorToUser, moveOperatorToAvailable, getOperatorById } = useDragDropOperator();
  const { getUserDisplayName } = useUsuariosUAU();
  const { empresaActiva } = useEmpresasContext();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  // Detectar cambio de empresa y limpiar estado
  useEffect(() => {
    if (empresaActiva?.empresa?.id) {
      // Limpiar usuario seleccionado al cambiar empresa
      setSelectedUserId(null);
      setActiveId(null);

      // Recargar datos para la nueva empresa
      loadInitialData();
    }
  }, [empresaActiva?.empresa?.id]);

  // Recargar operadores disponibles cuando cambie el usuario seleccionado
  useEffect(() => {
    if (selectedUserId) {
      loadOperadoresDisponibles(selectedUserId);
    } else {
      // Si no hay usuario seleccionado, limpiar operadores disponibles
      dispatch({ type: 'UPDATE_AVAILABLE_OPERATORS', payload: [] });
    }
  }, [selectedUserId]);

  const loadInitialData = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await AsignacionesUsuarioOperadorService.obtenerDatosDragDrop();
      dispatch({ type: 'SET_INITIAL_DATA', payload: data });
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error(t('errors.load_error'));
    }
  };

  const loadOperadoresDisponibles = async (usuarioId: number) => {
    try {
      const data = await AsignacionesUsuarioOperadorService.obtenerDatosDragDropParaUsuario(usuarioId);
      dispatch({ type: 'UPDATE_AVAILABLE_OPERATORS', payload: data.operadores_disponibles });
    } catch (error) {
      console.error('Error cargando operadores disponibles:', error);
      toast.error(t('admin.error_loading_operators'));
    }
  };

  // Manejar inicio de drag
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    dispatch({ type: 'SET_DRAGGING', payload: true });
  };

  // Manejar fin de drag
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    dispatch({ type: 'SET_DRAGGING', payload: false });

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!activeData) return;

    const operadorId = activeData.operador.id;
    const sourceUserId = activeData.sourceUserId;
    const isFromPool = activeData.isFromPool;

    // Caso 1: Mover desde pool disponible a usuario seleccionado
    if (isFromPool && overData?.type === 'assigned-pool' && selectedUserId) {
      moveOperatorToUser(operadorId, selectedUserId);
      toast.success(t('admin.operator_assigned_to', { user: getUserDisplayName(selectedUserId) }));
      return;
    }

    // Caso 2: Mover desde usuario a pool disponible
    if (!isFromPool && overData?.type === 'available-pool' && sourceUserId) {
      moveOperatorToAvailable(operadorId, sourceUserId);
      toast.success(t('admin.operator_unassigned'));
      return;
    }

    // Caso 3: Mover entre usuarios (futuro)
    if (!isFromPool && overData?.type === 'assigned-pool' && sourceUserId && overData.userId && overData.userId !== sourceUserId) {
      // Mover entre usuarios - por ahora no implementado
      toast(t('admin.move_between_users_not_implemented'));
      return;
    }
  };

  // Guardar cambios
  const handleSaveChanges = async () => {
    try {
      dispatch({ type: 'SET_SAVING', payload: true });

      const solicitud = AsignacionesUsuarioOperadorService.convertirDragDropALote(
        state.usuariosConOperadores,
        'Asignaciones de operadores actualizadas via drag & drop'
      );

      const resultado = await AsignacionesUsuarioOperadorService.asignarOperadoresLote(solicitud);

      if (resultado.exitoso) {
        dispatch({ type: 'MARK_SAVED', payload: new Date() });
        toast.success(resultado.mensaje);

        // Recargar datos para sincronizar
        await loadInitialData();

        // ✅ IMPORTANTE: Recargar operadores disponibles para el usuario activo
        if (selectedUserId) {
          await loadOperadoresDisponibles(selectedUserId);
        }
      } else {
        throw new Error(resultado.mensaje);
      }
    } catch (error: any) {
      dispatch({ type: 'SET_SAVE_ERROR', payload: error.message });
      toast.error(t('errors.save_error') + ': ' + error.message);
    }
  };

  // Obtener operadores del usuario seleccionado
  const selectedUserOperators = selectedUserId
    ? state.usuariosConOperadores.find(u => u.usuario_uau_id === selectedUserId)?.operadores || []
    : [];

  // Operador que se está arrastrando
  const activeOperator = activeId ? getOperatorById(parseInt(activeId.replace('operator-', ''))) : null;

  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con estadísticas y acciones */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('admin.user_operator_control')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {selectedUserId
                ? t('admin.managing_operators_for', { user: getUserDisplayName(selectedUserId) })
                : t('admin.select_user_to_manage_operators')
              }
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Estadísticas */}
            <div className="text-right">
              <div className="text-sm text-gray-500">{t('admin.assignment_progress')}</div>
              <div className="text-2xl font-bold text-gray-900">
                {state.operadoresAsignados}/{state.totalOperadores}
              </div>
              <div className="text-xs text-gray-400">
                {Math.round((state.operadoresAsignados / state.totalOperadores) * 100) || 0}% {t('admin.assigned')}
              </div>
            </div>

            {/* Botón guardar */}
            {selectedUserId && (
              <Button
                variant="primary"
                size="sm"
                disabled={!state.hasChanges || state.isSaving}
                loading={state.isSaving}
                onClick={handleSaveChanges}
              >
                {state.hasChanges ? t('common.save_changes') : t('common.saved')}
              </Button>
            )}
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="mt-4">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${Math.round((state.operadoresAsignados / state.totalOperadores) * 100) || 0}%`
              }}
            />
          </div>
        </div>
      </div>

      {/* Selector de usuarios */}
      <UserSelector
        selectedUserId={selectedUserId}
        onUserSelect={(userId) => setSelectedUserId(userId)}
        onUserDeselect={() => setSelectedUserId(null)}
      />

      {/* Pools de operadores - Solo si hay usuario seleccionado */}
      {selectedUserId && (
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pool de operadores disponibles */}
            <OperatorPool
              title={t('admin.available_operators')}
              operators={state.operadoresDisponibles}
              dropId="available-operators"
              emptyMessage={t('admin.all_operators_assigned')}
              emptyIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              }
              isFromPool={true}
              color="blue"
            />

            {/* Pool de operadores asignados al usuario */}
            <OperatorPool
              title={t('admin.operators_of', { user: getUserDisplayName(selectedUserId) })}
              operators={selectedUserOperators}
              dropId={`user-operator-${selectedUserId}`}
              emptyMessage={t('admin.no_operators_assigned')}
              emptyIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
              isFromPool={false}
              sourceUserId={selectedUserId}
              color="green"
            />
          </div>

          {/* Overlay para elemento que se arrastra */}
          <DragOverlay>
            {activeOperator ? (
              <DraggableOperator
                operador={activeOperator}
                isInPool={false}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Mensaje cuando no hay usuario seleccionado */}
      {!selectedUserId && (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('admin.select_user')}
          </h3>
          <p className="text-gray-500">
            {t('admin.select_user_operators_hint')}
          </p>
        </div>
      )}
    </div>
  );
}

// ===============================
// COMPONENTE PRINCIPAL (CON PROVIDER)
// ===============================

export function AdminUserOperatorView() {
  return (
    <DragDropOperatorProvider>
      <AdminUserOperatorViewContent />
    </DragDropOperatorProvider>
  );
}
